/* global BigInt */
/**
 * CheckoutPayCard — Link 20 D4: pay a TIM order in USDC through the checkout contract, from a browser wallet.
 *
 * Rendered for payment_prompt messages with metadata.payment_type === 'usdc_checkout' (MessageBubble): the
 * first card for a USDC order, and the "still owing" message after a partial payment (metadata.owing).
 * No wallet linking needed — any browser-extension wallet can pay. The steps and wording live in
 * lib/checkoutPay.js; this file is the wiring (wagmi actions, the order status endpoint) and the display.
 *
 * Status comes from GET /api/orders/{id}/payment-status (polled). The order is shown as paid only when TIM says
 * so; a reported tx (POST …/usdc-broadcast) is display-only on the server and pauses the card option there.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useConfig, useConnection } from 'wagmi';
import {
  connect, getBalance, getConnection, getPublicClient, readContract, simulateContract, switchChain, writeContract,
} from 'wagmi/actions';
import { BASE_CHAIN_ID, MESSAGES, formatUsdc, parseCard, payOrder } from '../lib/checkoutPay';
import '../styles/UsdcPayment.css';
import '../styles/CheckoutPay.css';

const POLL_FAST_MS = 4000;     // while a payment is on its way
const POLL_SLOW_MS = 20000;    // otherwise
const RECEIPT_TIMEOUT_MS = 10 * 60 * 1000;
// viem's own wait, not wagmi's action: wagmi's THROWS on a reverted receipt (to fetch the reason), and a revert
// must come back as { status: 'reverted' } so the card can say "nothing was taken" and lift the pause.
function waitReceipt(config, hash, onReplaced) {
  return getPublicClient(config, { chainId: BASE_CHAIN_ID })
    .waitForTransactionReceipt({ hash, onReplaced, timeout: RECEIPT_TIMEOUT_MS });
}

const LOCAL_SENT_MS = 15 * 60 * 1000;  // after a send from this page, keep showing "sent" even if TIM hasn't heard yet
const IDLE_POLL_STOP_MS = 30 * 60 * 1000;  // an untouched unpaid card stops polling after this (Pay restarts it)
const FAST_POLL_FOR_MS = 10 * 60 * 1000;   // fast polls for this long after a payment, then the slow rate

const STEP_LABEL = {
  connecting: 'Connecting your wallet…',
  checking: 'Checking…',
  checked: 'Checking…',
  approve: 'Approve this payment in your wallet…',
  approving: 'Waiting for the approval to confirm…',
  confirm: 'Confirm the payment in your wallet…',
  sending: 'Sending…',
};

function toUnits(decimalString) {
  // "0.060000" → 60000n (USDC base units); anything unparsable → null
  const m = /^(\d+)(?:\.(\d{0,6})\d*)?$/.exec(String(decimalString ?? ''));
  if (!m) return null;
  return BigInt(m[1]) * 1_000_000n + BigInt((m[2] || '').padEnd(6, '0'));
}

function short(addr) {
  return addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : '';
}

// The session cookie, plus the bearer token the rest of the app sends (utils/api.js) when it has one.
function authHeaders(extra = {}) {
  const h = { ...extra };
  try {
    const token = window.localStorage.getItem('session_token');
    if (token) h.Authorization = `Bearer ${token}`;
  } catch (e) { /* storage blocked: the cookie alone */ }
  return h;
}

async function getJson(url) {
  return fetch(url, { credentials: 'include', headers: authHeaders() });
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST', credentials: 'include',
    headers: authHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// Wait out wagmi's reconnect after a page load, so a click in the first moments doesn't use a stale account.
async function settledConnection(config) {
  for (let i = 0; i < 30; i += 1) {
    const c = getConnection(config);
    if (c.status !== 'reconnecting' && c.status !== 'connecting') return c;
    await new Promise((r) => setTimeout(r, 100));
  }
  return getConnection(config);
}

export default function CheckoutPayCard({ metadata, messageId, onPaymentConfirmed }) {
  const config = useConfig();
  const { address } = useConnection();
  const card = useMemo(() => parseCard(metadata), [metadata]);
  const orderId = card?.orderId;

  const [status, setStatus] = useState(null);       // the server's view of the order
  const [phase, setPhase] = useState('idle');       // idle | busy | sent | confirming | paid | stopped
  const phaseRef = useRef('idle');
  phaseRef.current = phase;
  const [step, setStep] = useState(null);
  const [notice, setNotice] = useState('');
  const [txHash, setTxHash] = useState(null);
  const [toPay, setToPay] = useState(null);         // on-chain amount owed, once checked (bigint)
  const busyRef = useRef(false);
  const watchingRef = useRef(null);
  const firstStatusRef = useRef(true);
  const notifiedRef = useRef(false);
  const mountedRef = useRef(true);
  const sentAtRef = useRef(0);
  const failedRef = useRef(new Set());              // txs this page saw fail (TIM may not have heard yet)
  const minedRef = useRef(new Set());               // txs seen mined successfully (no need to watch again)
  const lastActiveRef = useRef(Date.now());
  const onConfirmedRef = useRef(onPaymentConfirmed);
  onConfirmedRef.current = onPaymentConfirmed;

  // (set true here too: React's StrictMode runs the cleanup once in development)
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const statusUrl = orderId ? `/api/orders/${orderId}/payment-status` : null;
  // D4.1: TIM names the order's current Pay card; any other card for the order is an older one.
  const isReplaced = useCallback(
    (s) => Boolean(s && s.pay_card_message_id && messageId && s.pay_card_message_id !== messageId), [messageId]);

  const reportFailed = useCallback(
    (hash) => postJson(`/api/orders/${orderId}/usdc-failed`, { tx_hash: hash }), [orderId]);

  // Watch a tx we know about (e.g. after a page refresh) so a failure lifts the pause and says so.
  const watchTx = useCallback(async (hash) => {
    if (!hash || watchingRef.current === hash || minedRef.current.has(hash) || failedRef.current.has(hash)) return;
    watchingRef.current = hash;
    try {
      const rc = await waitReceipt(config, hash);
      if (rc?.status === 'success') minedRef.current.add(hash);
      if (rc?.status !== 'success' && mountedRef.current) {
        sentAtRef.current = 0;
        failedRef.current.add(hash);
        await reportFailed(hash).catch(() => {});
        // 'confirming' (the chain says nothing is owed) stays: a failed second attempt doesn't change that.
        setPhase((p) => (p === 'paid' || p === 'confirming' ? p : 'idle'));
        setNotice((n) => (n === MESSAGES.nothing_owed ? n : MESSAGES.failed_onchain));
      }
    } catch (e) {
      // Not found yet / timed out: the server's view (polling) decides.
    } finally {
      if (watchingRef.current === hash) watchingRef.current = null;
    }
  }, [config, reportFailed]);

  const fetchStatus = useCallback(async () => {
    if (!statusUrl) return null;
    try {
      const res = await getJson(statusUrl);
      if (res.status === 401 || res.status === 404) return { stop: true };
      if (!res.ok) return null;
      const data = await res.json();
      if (!mountedRef.current) return data;
      // An older card stops watching — unless the order is paid (it shows "Paid") or this page sent a payment
      // that's still on its way (it follows it to "Paid").
      if (isReplaced(data) && !data.paid && phaseRef.current !== 'sent') {
        setStatus(data);
        return { stop: true };
      }
      setStatus((prev) => {
        // What TIM has recorded changed (a payment arrived): the on-chain amount checked earlier is stale.
        if (prev && prev.paid_usdc !== data.paid_usdc) setToPay(null);
        return data;
      });
      const wasFirst = firstStatusRef.current;
      firstStatusRef.current = false;
      if (data.paid) {
        setPhase('paid');
        setNotice('');
        if (!wasFirst && !notifiedRef.current && onConfirmedRef.current) {
          notifiedRef.current = true;
          onConfirmedRef.current();
        }
      } else if (data.in_flight && failedRef.current.has(data.in_flight.tx_hash) && !data.in_flight.recorded) {
        // We saw this tx fail but TIM hasn't heard: tell it again; stay on Pay.
        reportFailed(data.in_flight.tx_hash).catch(() => {});
      } else if (data.in_flight) {
        if (!busyRef.current) {
          setPhase((p) => (p === 'confirming' ? p : 'sent'));
          setNotice((n) => (n === MESSAGES.nothing_owed ? n : ''));
          if (data.in_flight.tx_hash) {
            setTxHash(data.in_flight.tx_hash);
            watchTx(data.in_flight.tx_hash);
          }
        }
      } else if (!busyRef.current && Date.now() - sentAtRef.current > LOCAL_SENT_MS) {
        // Nothing on its way (a report expired, or the tx failed): back to Pay. payRemaining can't pay twice anyway.
        // ('confirming' — the chain says nothing is owed — never goes back: see below.)
        setPhase((p) => (p === 'sent' ? 'idle' : p));
      }
      return data;
    } catch (e) {
      return null;
    }
  }, [statusUrl, watchTx, reportFailed, isReplaced]);

  // Poll: fast while a payment is on its way, slow otherwise; never once paid, never for someone else's order,
  // and an untouched unpaid card stops after IDLE_POLL_STOP_MS (tapping Pay starts it again).
  useEffect(() => {
    if (!statusUrl || phase === 'paid') return undefined;
    lastActiveRef.current = Date.now();
    let timer = null;
    let stopped = false;
    const active = phase === 'sent' || phase === 'busy' || phase === 'confirming';
    const loop = async () => {
      const r = await fetchStatus();
      if (stopped || r?.stop) return;
      if (!active && Date.now() - lastActiveRef.current > IDLE_POLL_STOP_MS) return;
      const fast = active && Date.now() - lastActiveRef.current < FAST_POLL_FOR_MS;
      timer = setTimeout(loop, fast ? POLL_FAST_MS : POLL_SLOW_MS);
    };
    loop();
    return () => { stopped = true; if (timer) clearTimeout(timer); };
  }, [statusUrl, phase, fetchStatus]);

  const handlePay = async () => {
    if (!card || busyRef.current) return;
    busyRef.current = true;
    setPhase('busy');
    setNotice('');
    setStep('connecting');
    const deps = {
      connect: async () => {
        const now = await settledConnection(config);
        if (now.status === 'connected' && now.address) return now.address;
        // The app's injected() connector (window.ethereum); else a wallet that only announces itself (EIP-6963).
        const hasWindowWallet = typeof window !== 'undefined' && Boolean(window.ethereum);
        const connector = (hasWindowWallet && config.connectors.find((c) => c.id === 'injected'))
          || config.connectors.find((c) => c.type === 'injected' && c.id !== 'injected');
        if (!connector) { const err = new Error('no wallet'); err.code = 'NO_WALLET'; throw err; }
        const res = await connect(config, { connector });
        const first = res.accounts[0];
        return typeof first === 'string' ? first : first.address;
      },
      switchToBase: async () => {
        if (getConnection(config).chainId !== BASE_CHAIN_ID) await switchChain(config, { chainId: BASE_CHAIN_ID });
      },
      read: (p) => readContract(config, { ...p, chainId: BASE_CHAIN_ID }),
      ethBalance: async (a) => (await getBalance(config, { address: a, chainId: BASE_CHAIN_ID })).value,
      simulate: (p) => simulateContract(config, { ...p, chainId: BASE_CHAIN_ID }),
      write: (request) => writeContract(config, { ...request, chainId: BASE_CHAIN_ID }),
      waitReceipt: (hash, onReplaced) => waitReceipt(config, hash, onReplaced),
      reportBroadcast: (hash) => postJson(`/api/orders/${orderId}/usdc-broadcast`, { tx_hash: hash }),
      reportFailed: async (hash) => { failedRef.current.add(hash); return reportFailed(hash); },
      checkPayable: async () => {
        const res = await getJson(statusUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (mountedRef.current) setStatus(data);
        if (data.paid) return { payable: false, reason: MESSAGES.nothing_owed };
        if (isReplaced(data)) return { payable: false, reason: 'This payment card was replaced — use the one below.' };
        return { payable: Boolean(data.usdc_payable), reason: data.not_payable_reason };
      },
      sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
    };
    let result;
    try {
      result = await payOrder(deps, card, (s, info) => {
        if (!mountedRef.current) return;
        setStep(s);
        if (s === 'checked') setToPay(info.remaining);
        if (s === 'sending') { setTxHash(info.hash); watchingRef.current = info.hash; }
      });
    } finally {
      busyRef.current = false;
    }
    if (!mountedRef.current) return;
    setStep(null);
    if (result.outcome === 'sent' || result.outcome === 'sent_unconfirmed') {
      sentAtRef.current = Date.now();
      if (result.hash) setTxHash(result.hash);
      if (result.outcome === 'sent' && result.hash) minedRef.current.add(result.hash);
      setPhase('sent');
      fetchStatus();
    } else if (result.outcome === 'nothing_owed') {
      // The chain says this order is fully paid. That doesn't go back to "Pay" (or "pay by card") on this page,
      // whatever TIM says meanwhile — TIM may be behind, or the payment held for a look.
      setPhase('confirming');
      setNotice(result.message);
      fetchStatus();
    } else {
      if (result.error) console.warn('[CheckoutPayCard]', result.message, result.error);
      setPhase('idle');
      setNotice(result.message);
    }
  };

  if (!card) {
    return (
      <div className="usdc-payment-prompt checkout-card">
        <div className="payment-error">{MESSAGES.misconfigured}</div>
      </div>
    );
  }

  const explorer = txHash ? `https://basescan.org/tx/${txHash}` : null;
  const serverOwing = status ? toUnits(status.owing_usdc) : null;
  const owing = toPay ?? serverOwing ?? (metadata?.owing ? toUnits(metadata.owing_usdc ?? metadata.usdc_owing) : card.orderTotal);
  // Partly paid = locked to USDC (dust alone doesn't lock it: the card hint stays)
  const partlyPaid = status ? status.status === 'partially_paid' : Boolean(metadata?.owing);

  if (isReplaced(status) && phase !== 'busy' && phase !== 'sent' && phase !== 'paid' && !status?.paid) {
    return (
      <div className="usdc-payment-prompt checkout-card checkout-replaced">
        <p className="switch-hint">Replaced — use the payment card below.</p>
      </div>
    );
  }

  if (phase === 'paid') {
    return (
      <div className="usdc-payment-prompt checkout-card payment-confirmed">
        <div className="confirmed-content">
          <div className="confirmed-icon">✓</div>
          <h4>Paid</h4>
          <p>{formatUsdc(card.orderTotal)} USDC received — thank you.</p>
          {explorer && (
            <a className="paid-explorer-link" href={explorer} target="_blank" rel="noopener noreferrer">View on BaseScan ↗</a>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'sent' || phase === 'confirming') {
    const recorded = status?.in_flight?.recorded;
    return (
      <div className="usdc-payment-prompt checkout-card payment-sent">
        <div className="sent-content">
          <div className="sent-spinner" />
          <h4>{recorded || notice === MESSAGES.nothing_owed ? 'Payment received' : 'Payment sent'}</h4>
          <p>{notice || 'TIM is confirming it — usually within a minute or two. You can leave this page.'}</p>
          {explorer && (
            <p className="sent-hint">
              <a className="paid-explorer-link" href={explorer} target="_blank" rel="noopener noreferrer">View on BaseScan ↗</a>
            </p>
          )}
        </div>
      </div>
    );
  }

  const busy = phase === 'busy';
  if (!busy && status && !status.paid && !status.in_flight && status.usdc_payable === false) {
    // TIM says this order can't take a USDC payment now (gone to card, being charged, cancelled, a revision
    // that can't start yet): say why, and offer no button.
    return (
      <div className="usdc-payment-prompt checkout-card">
        <div className="payment-info" role="status">{status.not_payable_reason || MESSAGES.nothing_owed}</div>
      </div>
    );
  }
  return (
    <div className="usdc-payment-prompt checkout-card">
      <div className="chain-warning">USDC · BASE NETWORK</div>
      <div className="payment-amount">
        {partlyPaid ? 'Still owing: ' : 'Amount: '}
        <strong>{owing === null ? '…' : `${formatUsdc(owing)} USDC`}</strong>
      </div>
      {notice && (
        <div className={notice === MESSAGES.approved_not_paid ? 'payment-info' : 'payment-error'} role="alert">{notice}</div>
      )}
      <button className="pay-button" onClick={handlePay} disabled={busy}>
        {busy ? (STEP_LABEL[step] || 'Working…') : `Pay ${owing === null ? '' : `${formatUsdc(owing)} `}USDC`}
      </button>
      {!partlyPaid && !busy && (
        <p className="switch-hint">Want to pay by card instead? Just ask me in the chat.</p>
      )}
      <div className="payment-footer">
        {address && <p className="wallet-from">Paying from <code>{short(address)}</code></p>}
        <p className="confirmation-hint">
          Uses your browser wallet: approve this amount for TIM's checkout, then confirm the payment.
        </p>
      </div>
    </div>
  );
}
